import { useCallback, useEffect, useRef } from "react";
import { useBlocker } from "react-router";

/** The modal's intercept-close, as a page: block in-app navigation and tab
 *  close while a form is dirty. `onConfirmDiscard` runs only when the user
 *  confirms throwing the changes away (e.g. to drop a localStorage draft);
 *  call `markCompleted()` after a successful save so the post-save navigate
 *  isn't blocked.
 *
 *  The completed latch is a ref re-armed nowhere — safe because the hook
 *  lives in form components remounted per weighing (page `key=`); a reusable
 *  long-lived variant would need the StrictMode re-arm pattern (see learning
 *  strictmode-cleanup-poisons-mounted-ref).
 */
export function useUnsavedChangesGuard(
  isDirty: boolean,
  onConfirmDiscard?: () => void,
): { markCompleted: () => void } {
  const completedRef = useRef(false);

  const shouldBlock = useCallback(() => isDirty && !completedRef.current, [isDirty]);
  const blocker = useBlocker(shouldBlock);
  useEffect(() => {
    if (blocker.state !== "blocked") return;
    if (window.confirm("Discard unsaved changes?")) {
      onConfirmDiscard?.();
      blocker.proceed();
    } else {
      blocker.reset();
    }
  }, [blocker, onConfirmDiscard]);

  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const markCompleted = useCallback(() => {
    completedRef.current = true;
  }, []);
  return { markCompleted };
}
